// shaders.ts — GLSL ES 3.00 sources for the instanced-quad gaussian renderer.
//
// Per-gaussian data lives in an RGBA32F texture, four texels per gaussian:
//   t0 = (pos.xyz, 1)   t1 = (Σxx, Σxy, Σxz, Σyy)   t2 = (Σyz, Σzz, 0, 0)
//   t3 = (sRGB rgb, opacity)
// The only per-instance attribute is the gaussian index, which the CPU depth
// sort rewrites. The vertex shader projects the 3D covariance to a 2D screen
// covariance (EWA: Σ' = J·W·Σ·Wᵀ·Jᵀ + 0.3 px low-pass), takes its eigen
// decomposition, and stretches a unit quad along the two axes out to 3σ. The
// fragment shader evaluates the gaussian falloff and writes premultiplied
// colour so the blend is (ONE, ONE_MINUS_SRC_ALPHA), drawn back-to-front.
//
// With u_lensOn the splats are rendered as the refine trainer renders a
// training view (lens.ts, rstrain src/lens.rs): each 2D covariance gains the
// view's defocus α²(1/z−ρ)²·diag(fx², fy²) and motion smear u·uᵀ/12 (u = J·(φ×t
// + δ), t the gaussian in OpenCV camera axes), and its opacity is scaled by
// sqrt(det Σ / det Σ') so the blur conserves energy. project.ts mirrors it.

export const TEXELS_PER_GAUSSIAN = 4;
export const DATA_TEX_WIDTH = 2048; // texels; must be a multiple of 4
export const QUAD_EXTENT_SIGMA = 3;

export const VERTEX_SHADER = `#version 300 es
precision highp float;
precision highp int;
precision highp sampler2D;

uniform sampler2D u_data;
uniform mat4 u_view;
uniform mat4 u_proj;
uniform vec2 u_focal;     // fx, fy in device pixels
uniform vec2 u_viewport;  // width, height in device pixels
uniform int u_lensOn;     // non-zero: blur with the lens below
uniform vec2 u_lens;      // focus plane rho (1/m), aperture coefficient alpha (m)
uniform vec3 u_lensPhi;   // rotation over the exposure (rad, OpenCV camera axes)
uniform vec3 u_lensDelta; // translation over the exposure (m, OpenCV camera axes)

in vec2 a_quad;           // corner in [-3,3]^2 (units of sigma)
in uint a_index;          // gaussian index (instanced, depth-sorted)

out vec4 v_color;
out vec2 v_pos;

vec4 fetch(uint i, uint k) {
  uint t = i * 4u + k;
  return texelFetch(u_data, ivec2(int(t % ${DATA_TEX_WIDTH}u), int(t / ${DATA_TEX_WIDTH}u)), 0);
}

void main() {
  vec4 t0 = fetch(a_index, 0u);
  vec4 t1 = fetch(a_index, 1u);
  vec4 t2 = fetch(a_index, 2u);
  vec4 t3 = fetch(a_index, 3u);

  vec4 cam = u_view * vec4(t0.xyz, 1.0);
  vec4 clip = u_proj * cam;
  float clipBound = 1.2 * clip.w;
  if (cam.z > -0.01 || clip.x < -clipBound || clip.x > clipBound || clip.y < -clipBound || clip.y > clipBound) {
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0); // culled: outside the clip volume
    return;
  }

  mat3 sigma = mat3(t1.x, t1.y, t1.z,
                    t1.y, t1.w, t2.x,
                    t1.z, t2.x, t2.y);
  float z = cam.z;
  // Columns are the rows of the screen-space Jacobian of (u,v) = f·(x,y)/(-z),
  // both rows negated (Σ' is invariant under J → -J).
  mat3 J = mat3(u_focal.x / z, 0.0, -(u_focal.x * cam.x) / (z * z),
                0.0, u_focal.y / z, -(u_focal.y * cam.y) / (z * z),
                0.0, 0.0, 0.0);
  mat3 T = transpose(mat3(u_view)) * J;
  mat3 cov2d = transpose(T) * sigma * T;
  float a = cov2d[0][0] + 0.3;
  float b = cov2d[0][1];
  float c = cov2d[1][1] + 0.3;

  float opScale = 1.0;
  if (u_lensOn != 0) {
    vec3 t = vec3(cam.x, -cam.y, -cam.z); // GL view → OpenCV camera axes
    float ad = u_lens.y * (1.0 / t.z - u_lens.x);
    float kx = ad * u_focal.x;
    float ky = ad * u_focal.y;
    vec3 d = cross(u_lensPhi, t) + u_lensDelta;
    float u0 = (u_focal.x / t.z) * d.x - (u_focal.x * t.x / (t.z * t.z)) * d.z;
    float u1 = (u_focal.y / t.z) * d.y - (u_focal.y * t.y / (t.z * t.z)) * d.z;
    float det0 = a * c - b * b;
    a += kx * kx + u0 * u0 / 12.0;
    b -= u0 * u1 / 12.0; // screen y is up here, down in OpenCV: the cross term flips
    c += ky * ky + u1 * u1 / 12.0;
    opScale = sqrt(det0 / (a * c - b * b));
  }

  float mid = 0.5 * (a + c);
  float radius = length(vec2(0.5 * (a - c), b));
  float lambda1 = mid + radius;
  float lambda2 = max(mid - radius, 0.1);
  vec2 diag = abs(b) > 1e-8 ? normalize(vec2(b, lambda1 - a)) : (a >= c ? vec2(1.0, 0.0) : vec2(0.0, 1.0));
  // One sigma along each principal axis, in device pixels.
  vec2 majorAxis = min(sqrt(lambda1), 1024.0) * diag;
  vec2 minorAxis = min(sqrt(lambda2), 1024.0) * vec2(diag.y, -diag.x);

  vec2 center = clip.xy / clip.w;
  v_color = vec4(t3.rgb, t3.a * opScale);
  v_pos = a_quad;
  vec2 offsetPx = a_quad.x * majorAxis + a_quad.y * minorAxis;
  gl_Position = vec4(center + 2.0 * offsetPx / u_viewport, 0.0, 1.0);
}
`;

export const FRAGMENT_SHADER = `#version 300 es
precision highp float;

in vec4 v_color;
in vec2 v_pos;
out vec4 fragColor;

void main() {
  float r2 = dot(v_pos, v_pos); // squared Mahalanobis distance
  if (r2 > 9.0) discard;
  float alpha = exp(-0.5 * r2) * v_color.a;
  if (alpha < 1.0 / 255.0) discard;
  fragColor = vec4(v_color.rgb * alpha, alpha);
}
`;
